import { StrictMode } from 'react';
import posthog from 'posthog-js';
import { PostHogErrorBoundary, PostHogProvider } from '@posthog/react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import router from './router/router.tsx';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import translations from './locale/translations.ts';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/context/AuthContext.tsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
// import * as Sentry from '@sentry/react';
// Supports weights 300-700
import '@fontsource-variable/cormorant-garamond/wght.css';
import '@fontsource-variable/cormorant-garamond/wght-italic.css';
// Supports weights 100-900
import '@fontsource-variable/dm-sans/wght.css';
import 'react-day-picker/src/style.css';
import './index.css';
import { ThemeProvider } from '@/context/ThemeContext.tsx';
import { DnaRefinementProvider } from '@/context/DnaRefinementContext.tsx';
import { ToastProvider } from '@/context/ToastContext.tsx';
import ToastList from '@/components/ui/ToastList/ToastList.tsx';

dayjs.extend(utc);

i18n.use(initReactI18next).init({
  resources: translations,
  lng: 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});

export const queryClient = new QueryClient();

const posthogToken = import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN as string | undefined;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined;

if (posthogToken && posthogHost) {
  posthog.init(posthogToken, {
    api_host: posthogHost,
    defaults: '2026-01-30',
    disable_session_recording: true,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
  });
} else if (import.meta.env.DEV) {
  console.error(
    'VITE_PUBLIC_POSTHOG_PROJECT_TOKEN or VITE_PUBLIC_POSTHOG_HOST variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once VITE_PUBLIC_POSTHOG_PROJECT_TOKEN and VITE_PUBLIC_POSTHOG_HOST are configured'
  );
}

//
// Sentry.init({
//   dsn: import.meta.env.VITE_SENTRY_DSN,
//   integrations: [
//     Sentry.captureConsoleIntegration({
//       levels: ['error'],
//     }),
//   ],
// });

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <PostHogErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AuthProvider>
              <DnaRefinementProvider>
                <ToastProvider>
                  <RouterProvider router={router} />
                  <ToastList />
                </ToastProvider>
              </DnaRefinementProvider>
            </AuthProvider>
            <div id="dropdown-wrapper"></div>
          </ThemeProvider>
        </QueryClientProvider>
      </PostHogErrorBoundary>
    </PostHogProvider>
  </StrictMode>
);
